import React, { useState } from 'react';

/** '' = no subject / no due date. */
export interface HomeworkDraft {
  subject: string;
  task: string;
  due_date: string; // YYYY-MM-DD, '' when unset
}

/** '' = no subject. `date` is required before a draft can be added. */
export interface ExamDraft {
  subject: string;
  title: string; // '' = untitled ("Exam")
  date: string; // YYYY-MM-DD
}

interface HomeworkExamFieldsProps {
  homeworks: HomeworkDraft[];
  exams: ExamDraft[];
  onHomeworksChange: (next: HomeworkDraft[]) => void;
  onExamsChange: (next: ExamDraft[]) => void;
  /** Subject options for the selects: [key, label] pairs. */
  subjectOptions: { key: string; label: string }[];
}

export const MAX_HOMEWORKS = 10;
export const MAX_EXAMS = 5;

const FALLBACK_LABELS: Record<string, string> = {
  maths: 'Maths',
  science: 'Science',
  social: 'Social',
};

/** Chip washes per subject — matches the teacher-app mock (maths violet,
 *  science sky, social amber; slate for "no subject"). */
const CHIP_STYLES: Record<string, string> = {
  maths: 'bg-violet-100 text-violet-700',
  science: 'bg-sky-100 text-sky-700',
  social: 'bg-amber-100 text-amber-700',
};

const chipClass = (subject: string): string =>
  CHIP_STYLES[subject] || 'bg-slate-100 text-slate-600';

const todayISO = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/** "2026-07-10" → "Thu 10 Jul" (falls back to the raw string). */
const formatDayLabel = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
};

const SubjectChip: React.FC<{ subject: string; labelFor: (key: string) => string }> = ({
  subject,
  labelFor,
}) => (
  <span
    className={`whitespace-nowrap rounded-lg px-2.5 py-1 text-[0.7rem] font-bold ${chipClass(subject)}`}
  >
    {subject ? labelFor(subject) : 'General'}
  </span>
);

const RemoveButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="-my-1 ml-auto rounded-lg p-2 text-lg leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
  >
    ×
  </button>
);

const inputClass =
  'rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-700 focus:border-violet-400 focus:outline-none';

/** Two stacked cards for the weekly plan: multi-subject homework + exams. */
const HomeworkExamFields: React.FC<HomeworkExamFieldsProps> = ({
  homeworks,
  exams,
  onHomeworksChange,
  onExamsChange,
  subjectOptions,
}) => {
  // Add-row form state (local; a draft only reaches the parent on "Add").
  const [hwSubject, setHwSubject] = useState('');
  const [hwTask, setHwTask] = useState('');
  const [hwDue, setHwDue] = useState('');
  const [exSubject, setExSubject] = useState('');
  const [exTitle, setExTitle] = useState('');
  const [exDate, setExDate] = useState('');

  const labelFor = (key: string): string =>
    subjectOptions.find((s) => s.key === key)?.label || FALLBACK_LABELS[key] || key;

  const canAddHomework = hwTask.trim().length > 0 && homeworks.length < MAX_HOMEWORKS;
  const canAddExam = exDate.length > 0 && exams.length < MAX_EXAMS;

  const addHomework = () => {
    if (!canAddHomework) return;
    onHomeworksChange([...homeworks, { subject: hwSubject, task: hwTask.trim(), due_date: hwDue }]);
    // Keep the subject picked — teachers often add a few tasks per subject.
    setHwTask('');
    setHwDue('');
  };

  const addExam = () => {
    if (!canAddExam) return;
    onExamsChange([...exams, { subject: exSubject, title: exTitle.trim(), date: exDate }]);
    setExTitle('');
    setExDate('');
  };

  return (
    <div className="space-y-4">
      {/* Homework card */}
      <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4 sm:p-5">
        <h2 className="text-base font-bold text-slate-900">Homework</h2>
        <p className="mt-0.5 text-xs text-slate-400">
          Set across any subject — the curiosity helps students think it through.
        </p>

        <div className="mt-3 space-y-2.5">
          {homeworks.length === 0 && (
            <p className="text-sm text-slate-400">No homework set this week.</p>
          )}
          {homeworks.map((hw, i) => (
            <div
              key={`${hw.subject}-${hw.task}-${i}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-[#E9E6F4] bg-white p-3.5"
            >
              <SubjectChip subject={hw.subject} labelFor={labelFor} />
              <span className="min-w-0 flex-1 basis-40">
                <span className="block truncate text-sm font-semibold text-slate-800">
                  {hw.task}
                </span>
              </span>
              {hw.due_date && (
                <span className="whitespace-nowrap font-mono text-xs font-semibold text-slate-500">
                  Due {formatDayLabel(hw.due_date)}
                </span>
              )}
              <RemoveButton
                label={`Remove homework: ${hw.task}`}
                onClick={() => onHomeworksChange(homeworks.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>

        {homeworks.length < MAX_HOMEWORKS ? (
          <div className="mt-3 rounded-xl border-[1.5px] border-dashed border-[#E9E6F4] bg-violet-50/50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={hwSubject}
                onChange={(e) => setHwSubject(e.target.value)}
                className={inputClass}
                aria-label="Homework subject"
              >
                <option value="">No subject</option>
                {subjectOptions.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={hwTask}
                onChange={(e) => setHwTask(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addHomework();
                  }
                }}
                placeholder="e.g. Exercise 4.2, questions 1–5"
                className={`min-w-[10rem] flex-1 ${inputClass}`}
                aria-label="Homework task"
              />
              <input
                type="date"
                value={hwDue}
                min={todayISO()}
                onChange={(e) => setHwDue(e.target.value)}
                className={inputClass}
                aria-label="Due date (optional)"
              />
              <button
                type="button"
                onClick={addHomework}
                disabled={!canAddHomework}
                className="rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Add
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-400">
            You've reached the limit of {MAX_HOMEWORKS} homework tasks — remove one to add another.
          </p>
        )}
      </div>

      {/* Exams card */}
      <div className="rounded-2xl border border-[#E9E6F4] bg-white p-4 sm:p-5">
        <h2 className="text-base font-bold text-slate-900">Exams</h2>
        <p className="mt-0.5 text-xs text-slate-400">
          Students get gentle revision nudges as the date nears.
        </p>

        <div className="mt-3 space-y-2.5">
          {exams.length === 0 && <p className="text-sm text-slate-400">No exams scheduled.</p>}
          {exams.map((ex, i) => (
            <div
              key={`${ex.subject}-${ex.date}-${i}`}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-[#E9E6F4] bg-white p-3.5"
            >
              <SubjectChip subject={ex.subject} labelFor={labelFor} />
              <span className="min-w-0 flex-1 basis-40">
                <span className="block truncate text-sm font-semibold text-slate-800">
                  {ex.title.trim() || 'Exam'}
                </span>
              </span>
              <span className="whitespace-nowrap font-mono text-xs font-semibold text-slate-500">
                {ex.date ? formatDayLabel(ex.date) : 'No date'}
              </span>
              <RemoveButton
                label={`Remove exam: ${ex.title.trim() || 'Exam'}`}
                onClick={() => onExamsChange(exams.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>

        {exams.length < MAX_EXAMS ? (
          <div className="mt-3 rounded-xl border-[1.5px] border-dashed border-[#E9E6F4] bg-violet-50/50 p-3">
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={exSubject}
                onChange={(e) => setExSubject(e.target.value)}
                className={inputClass}
                aria-label="Exam subject"
              >
                <option value="">No subject</option>
                {subjectOptions.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label}
                  </option>
                ))}
              </select>
              <input
                type="text"
                value={exTitle}
                maxLength={120}
                onChange={(e) => setExTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addExam();
                  }
                }}
                placeholder="e.g. Unit test — Chapters 1–4"
                className={`min-w-[10rem] flex-1 ${inputClass}`}
                aria-label="Exam title (optional)"
              />
              <input
                type="date"
                value={exDate}
                min={todayISO()}
                onChange={(e) => setExDate(e.target.value)}
                className={inputClass}
                aria-label="Exam date"
              />
              <button
                type="button"
                onClick={addExam}
                disabled={!canAddExam}
                className="rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Add
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-400">
            You've reached the limit of {MAX_EXAMS} exams — remove one to add another.
          </p>
        )}
      </div>
    </div>
  );
};

export default HomeworkExamFields;
